import React from 'react'
import ReactDOM from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'

import App from './App'
import { store } from './store'
import './globals.css'

// Get Clerk publishable key from environment
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!clerkPubKey) {
  console.warn('⚠️ VITE_CLERK_PUBLISHABLE_KEY not set - Clerk authentication will not work')
  console.warn('💡 Create a .env file in src/frontend/ with VITE_CLERK_PUBLISHABLE_KEY=your_key')
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false
        }
        return failureCount < 3
      },
    },
    mutations: {
      retry: false,
    },
  },
})

// Only render ClerkProvider if we have a key, otherwise render app without auth
const AppWithProviders = clerkPubKey ? (
  <ClerkProvider 
    publishableKey={clerkPubKey}
    signInFallbackRedirectUrl="/dashboard"
    signUpFallbackRedirectUrl="/dashboard"
  >
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <App />
          <Toaster 
            position="top-right"
            richColors
            closeButton
            duration={5000}
          />
        </BrowserRouter>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </Provider>
  </ClerkProvider>
) : (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>⚠️ Clerk Not Configured</h1>
          <p>Please set VITE_CLERK_PUBLISHABLE_KEY in your .env file</p>
          <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '1rem' }}>
            Create a .env file in src/frontend/ with:<br />
            <code>VITE_CLERK_PUBLISHABLE_KEY=your_clerk_publishable_key</code>
          </p>
        </div>
        <Toaster 
          position="top-right"
          richColors
          closeButton
          duration={5000}
        />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </Provider>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {AppWithProviders}
  </React.StrictMode>,
)